import { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Today as TodayIcon,
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format, isToday, isPast } from 'date-fns';

interface TemporalDatePickerProps {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  academicYearStart?: Date;
  academicYearEnd?: Date;
}

/**
 * TemporalDatePicker Component
 * 
 * Allows users to select a specific date to view historical timetable data.
 * Shows indicators when viewing historical data vs current active timetable.
 * 
 * Requirements: 3B.6, 3B.7
 */
export function TemporalDatePicker({
  selectedDate,
  onDateChange,
  academicYearStart,
  academicYearEnd,
}: TemporalDatePickerProps) {
  const [dateInputValue, setDateInputValue] = useState<string>(
    selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''
  );

  const isViewingHistorical = selectedDate && !isToday(selectedDate);
  const isViewingPast = selectedDate && isPast(selectedDate) && !isToday(selectedDate);

  const handleDateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDateInputValue(value);

    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Validate date is within academic year if bounds are provided
        if (academicYearStart && date < academicYearStart) {
          return;
        }
        if (academicYearEnd && date > academicYearEnd) {
          return;
        }
        onDateChange(date);
      }
    }
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateInputValue(format(today, 'yyyy-MM-dd'));
    onDateChange(today);
  };

  const handleClearDate = () => {
    setDateInputValue('');
    onDateChange(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: 1,
        borderColor: isViewingHistorical ? 'warning.main' : 'divider',
        backgroundColor: isViewingHistorical ? 'warning.lighter' : 'background.paper',
        transition: 'all 0.3s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Date Picker Input */}
        <TextField
          type="date"
          label="View Timetable for Date"
          value={dateInputValue}
          onChange={handleDateInputChange}
          size="small"
          sx={{ minWidth: 200 }}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: academicYearStart ? format(academicYearStart, 'yyyy-MM-dd') : undefined,
            max: academicYearEnd ? format(academicYearEnd, 'yyyy-MM-dd') : undefined,
          }}
        />

        {/* Today Button */}
        <Tooltip title="View today's timetable">
          <IconButton
            onClick={handleTodayClick}
            size="small"
            color={!selectedDate || isToday(selectedDate) ? 'primary' : 'default'}
          >
            <TodayIcon />
          </IconButton>
        </Tooltip>

        {/* Clear Button */}
        {selectedDate && (
          <Tooltip title="Clear date selection (view current active timetable)">
            <IconButton onClick={handleClearDate} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Historical Data Indicator */}
        {isViewingHistorical && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <HistoryIcon color="warning" fontSize="small" />
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
              Viewing Historical Data
            </Typography>
            <Chip
              label={isViewingPast ? 'Past' : 'Future'}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Box>
        )}

        {/* Current Active Indicator */}
        {!selectedDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Chip
              label="Current Active Timetable"
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>
        )}

        {/* Selected Date Display */}
        {selectedDate && !isViewingHistorical && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Chip
              label={`Viewing: ${format(selectedDate, 'MMM d, yyyy')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      {/* Help Text */}
      {isViewingHistorical && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {isViewingPast
            ? 'You are viewing the timetable as it was on this date. This shows which teachers and subjects were assigned at that time.'
            : 'You are viewing a future date. This shows the timetable that will be active on that date based on planned structural changes.'}
        </Typography>
      )}

      {!selectedDate && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing the current active timetable (records with no end date). Select a date above to view historical or future timetable versions.
        </Typography>
      )}
    </Paper>
  );
}
